import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon';
import UserRemoveIcon from '@heroicons/react/outline/UserRemoveIcon';
import InfiniteScroll from 'react-infinite-scroller';
import { hasScopesPermission } from '@tribeplatform/gql-client';
import { SpaceRoleType } from '@tribeplatform/gql-client/types';
import { useRemoveSpaceMember, useSpace, useSpaceMembers, useSpaceRoles, useUpdateMemberSpaceRole, } from '@tribeplatform/react-sdk/hooks';
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils';
import { confirm } from '@tribeplatform/react-ui-kit/Dialog';
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown';
import { GridList } from '@tribeplatform/react-ui-kit/Layout';
import { T, useI18n } from '../i18n';
import { MemberListLoading } from './CommunityMemberList';
import { EmptyState } from './EmptyState';
import { FilterBar } from './FilterBar';
import { MemberCard } from './MemberCard';
export const SpaceMemberList = ({ spaceId }) => {
    const intl = useI18n();
    const [filters, setFilters] = useState({});
    const { data: space } = useSpace({ variables: { id: spaceId } });
    const { data, fetchNextPage, hasNextPage, isFetched, isLoading, isFetchingNextPage, } = useSpaceMembers({
        fields: {
            member: {
                profilePicture: 'basic',
                banner: 'basic',
                role: 'basic',
                badges: 'default',
            },
            role: 'basic',
        },
        variables: { limit: 30, spaceId, ...filters },
        useInfiniteQueryOptions: {
            refetchOnMount: 'always',
        },
    });
    const { data: roles } = useSpaceRoles({
        variables: {
            spaceId,
        },
    });
    const { mutateAsync: removeSpaceMember } = useRemoveSpaceMember();
    const { mutateAsync: updateMemberSpaceRole } = useUpdateMemberSpaceRole();
    const [canUpdateMemberSpaceRole, canRemoveSpaceMembers] = hasScopesPermission(space, ['updateMemberSpaceRole', 'removeSpaceMembers']);
    const adminRole = roles === null || roles === void 0 ? void 0 : roles.find(role => role.type === SpaceRoleType.admin);
    const memberRole = roles === null || roles === void 0 ? void 0 : roles.find(role => role.type === SpaceRoleType.member);
    const getOtherMemberRole = (spaceMember) => { var _a; return ((_a = spaceMember === null || spaceMember === void 0 ? void 0 : spaceMember.role) === null || _a === void 0 ? void 0 : _a.id) === (adminRole === null || adminRole === void 0 ? void 0 : adminRole.id) ? memberRole : adminRole; };
    const getMemberCardActions = (spaceMember) => {
        var _a, _b;
        if (!(canUpdateMemberSpaceRole || canRemoveSpaceMembers)) {
            return null;
        }
        const { member } = spaceMember;
        const otherRole = getOtherMemberRole(spaceMember);
        return (_jsxs(_Fragment, { children: [canRemoveSpaceMembers && (_jsx(Dropdown.Item, { leadingIcon: _jsx(UserRemoveIcon, {}, void 0), onClick: async () => {
                        const confirmed = await confirm({
                            title: intl.$t({
                                id: 'Space.Members.Remove.Confirmation',
                                defaultMessage: 'Are you sure you want to remove {member_name} ({member_username}) from this {SPACE}?',
                            }, {
                                member_name: member === null || member === void 0 ? void 0 : member.name,
                                member_username: member === null || member === void 0 ? void 0 : member.username,
                            }),
                            proceedLabel: intl.$t({
                                id: 'Generics.Remove',
                                defaultMessage: 'Remove',
                            }),
                            danger: true,
                        });
                        if (!confirmed) {
                            return;
                        }
                        await removeSpaceMember({
                            spaceId,
                            memberIds: [member === null || member === void 0 ? void 0 : member.id],
                        });
                    }, children: _jsx(T, { id: "Generics.RemoveMember", defaultMessage: "Remove {MEMBER}" }, void 0) }, void 0)), canUpdateMemberSpaceRole && (_jsx(Dropdown.Item, { leadingIcon: _jsx(ShieldCheckIcon, {}, void 0), onClick: async () => {
                        var _a;
                        const confirmed = await confirm({
                            title: intl.$t({
                                id: 'Space.Members.ChangeRole.Confirmation',
                                defaultMessage: 'Are you sure you want to change role of {member_name} ({member_username}) to {role_name, select, admin {space admin} member {space member} other {space {role_name}}}?',
                            }, {
                                member_name: member === null || member === void 0 ? void 0 : member.name,
                                member_username: member === null || member === void 0 ? void 0 : member.username,
                                role_name: (_a = otherRole === null || otherRole === void 0 ? void 0 : otherRole.name) === null || _a === void 0 ? void 0 : _a.toLowerCase(),
                            }),
                            proceedLabel: intl.$t({
                                id: 'Space.Members.ChangeRole.Proceed',
                                defaultMessage: 'Change role',
                            }),
                        });
                        if (!confirmed) {
                            return;
                        }
                        await updateMemberSpaceRole({
                            memberId: member === null || member === void 0 ? void 0 : member.id,
                            spaceId,
                            input: {
                                roleId: otherRole === null || otherRole === void 0 ? void 0 : otherRole.id,
                            },
                        });
                    }, children: _jsx(T, { defaultMessage: "Change role to {role_name, select, admin {space admin} member {space member} other {space {role_name}}}", id: "Space.Members.ChangeRole", values: {
                            role_name: (_b = (_a = getOtherMemberRole(spaceMember)) === null || _a === void 0 ? void 0 : _a.name) === null || _b === void 0 ? void 0 : _b.toLowerCase(),
                        } }, void 0) }, void 0))] }, void 0));
    };
    const { nodes: members = [] } = simplifyPaginatedResult(data);
    return (_jsx(InfiniteScroll, { pageStart: 0, loadMore: fetchNextPage, hasMore: hasNextPage || false, threshold: 800, children: isFetched && !(members === null || members === void 0 ? void 0 : members.length) && !isLoading ? (_jsx(EmptyState, {}, void 0)) : (_jsxs(_Fragment, { children: [false && _jsx(FilterBar, { setFilters: setFilters }, void 0), _jsxs(GridList, { columns: 4, className: "items-stretch", children: [members
                            .filter(spaceMember => spaceMember === null || spaceMember === void 0 ? void 0 : spaceMember.member)
                            .map(spaceMember => {
                            const { member, role } = spaceMember;
                            return (_jsx(GridList.Item, { children: _jsx(MemberCard, { member: member, extraBadge: (role === null || role === void 0 ? void 0 : role.type) === SpaceRoleType.admin
                                        ? intl.$t({
                                            id: 'Space.Members.SpaceAdmin',
                                            defaultMessage: '{SPACE_CC} admin',
                                        })
                                        : null, actions: getMemberCardActions(spaceMember) }, void 0) }, member.id));
                        }), !isFetched || isLoading || isFetchingNextPage ? (_jsx(MemberListLoading, {}, void 0)) : null] }, void 0)] }, void 0)) }, void 0));
};
//# sourceMappingURL=SpaceMemberList.js.map